import Box from "@/common/components/Box";
import BoxWithHeader from "@/common/components/BoxWithHeader";
import Button from "@/common/components/Button";
import Checkbox from "@/common/components/Checkbox";
import { ErrorMessageWrapper } from "@/common/components/ErrorMessageWrapper";
import { ModalMessageSimple } from "@/common/components/ModalMessageSimple";
import { ShowHeader } from "@/common/components/ShowHeader";
import TextArea from "@/common/components/TextArea";
import { UnitTextField } from "@/common/components/UnitTextField";
import { useGoBack } from "@/common/hooks/useGoBack";
import useUserPermissions from "@/common/hooks/useUserPermissions";
import { executeAtEndOfCallStack } from "@/common/utils/executeAtEndOfCallStack";
import { FormTransformers } from "@/common/utils/formTransformers";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { Controller, FormProvider, SubmitHandler, useForm } from "react-hook-form";
import { useParams } from "react-router-dom";
import { useMaintenanceForwarding } from "../../../../hooks/forwardings/useMaintenanceForwarding";
import { useAdvanceMaintenanceDirectiveDiagnosticEntry } from "../../hooks/useAdvanceMaintenanceDirectiveDiagnosticEntry";
import { useUpdateMaintenanceDirectiveDiagnosticEntry } from "../../hooks/useUpdateMaintenanceDirectiveDiagnosticEntry";
import { updateDiagnosticEntrySchema, UpdateDiagnosticEntryValidationSchema } from "../../schemas/updateDiagnosticEntry.schema";
import MaintenanceWorkflowDiagnosticEntryUpdateLoading from "./components/MaintenanceWorkflowDiagnosticEntryUpdateLoading";
import DiagnosticEntryUpdateObjectiveAttributes from "./components/sections/ObjectiveAttributes";
import ReviewDescription from "@/common/components/ReviewDescription";
import { MaintenanceForwardingsDirectiveEntryKlass } from "../../../../types/forwardings/maintenanceForwardingsDirectiveEntryKlasses.type";
import { MaintenanceForwardingDirectiveEntryStatus } from "@/features/maintenance/features/workflows/types/forwardings/maintenanceForwarding.type";

const MaintenanceWorkflowDiagnosticEntryUpdate = () => {
  const { workflowId, forwardingId } = useParams();

  const { hasUserPermission, isUserPermissionsPending } = useUserPermissions();

  const queryClient = useQueryClient();

  const goBack = useGoBack();

  const [formDisabled, setFormDisabled] = useState(false);

  const [messages, setMessages] = useState<string[] | boolean>(false);

  const {
    data: forwarding,
    isPending: forwardingIsPending,
    error: forwardingError = {} as any,
    refetch: refetchForwarding,
    dataUpdatedAt: forwardingUpdatedAt,
  } = useMaintenanceForwarding<
    MaintenanceForwardingsDirectiveEntryKlass.DiagnosticEntry
  >(forwardingId);

  const formMethods = useForm<UpdateDiagnosticEntryValidationSchema>({
    resolver: zodResolver(updateDiagnosticEntrySchema),
    disabled: formDisabled,
  });

  const {
    control,
    handleSubmit,
    reset,
    setError,
    formState: {
      errors,
    },
  } = formMethods;

  const {
    mutate: updateMaintenanceDiagnosticEntry,
    isPending: updateMaintenanceDiagnosticEntryIsPending,
  } = useUpdateMaintenanceDirectiveDiagnosticEntry({
    directiveEntryId: forwarding?.directive_entry?.id,
    options: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['maintenanceForwarding', forwardingId] });
      },
      onError: (error) => {
        FormTransformers.errorsTransformer({
          setError,
          setMessages,
        })(error);
      },
    },
  });

  const {
    mutate: advanceDiagnosticEntry,
    isPending: advanceDiagnosticEntryIsPending,
  } = useAdvanceMaintenanceDirectiveDiagnosticEntry({
    directiveEntryId: forwarding?.directive_entry?.id,
    options: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['maintenanceForwarding', forwardingId] });

        goBack({
          fallback: `/maintenance-workflows/${workflowId}`,
          searchLastRouteFromFallback: true,
        });
      },
    }
  });


  const onSubmit: SubmitHandler<UpdateDiagnosticEntryValidationSchema> = (data) => updateMaintenanceDiagnosticEntry(data);

  const submit = () => executeAtEndOfCallStack(handleSubmit(onSubmit));

  const isLoading = useMemo(() => {
    return isUserPermissionsPending || forwardingIsPending;
  }, [isUserPermissionsPending, forwardingIsPending]);

  const hasPermissionToUpdate = useMemo(() => {
    return hasUserPermission('Establishment::Maintenance::Directive::DiagnosticEntries::Update');
  }, [isUserPermissionsPending]);

  const isEveryRequiredListSummaryPassed = useMemo(() => {
    return forwarding?.directive_entry?.requirement_list_summary?.steps?.every((step) => step.passed);
  }, [forwardingUpdatedAt]);

  useEffect(() => {
    if (forwardingIsPending) {
      return;
    }

    reset({
      technical_response: forwarding?.directive_entry.technical_response,
      deadline_to_complete_in_days: forwarding?.directive_entry?.deadline_to_complete_in_days
        ? `${forwarding?.directive_entry?.deadline_to_complete_in_days} dia(s)`
        : undefined,
      objectives_attributes: forwarding?.directive_entry?.objectives?.map((objective) => ({
        id: objective.id,
        does_not_need_estimate: objective.does_not_need_estimate,
      })),
    });
  }, [forwardingIsPending]);

  useEffect(() => {
    setFormDisabled(updateMaintenanceDiagnosticEntryIsPending);
  }, [updateMaintenanceDiagnosticEntryIsPending]);

  const isDiagnosticEntryDisapproved =
    forwarding?.directive_entry?.last_review?.state === 'disapproved' &&
    forwarding?.directive_entry.status === MaintenanceForwardingDirectiveEntryStatus.InProgress;

  return (
    <FormProvider {...formMethods}>
      <div className="inner-screen">
        <ShowHeader
          goBack
          goBackProps={{
            fallback: `/maintenance-workflows/${workflowId}/Maintenance::Directive::DiagnosticEntry`,
            searchLastRouteFromFallback: true,
          }}
          isLoading={isLoading}
          title="Diagnóstico e Orçamento"
          titleClassName="text-lg font-medium"
          buttons={!isLoading && hasPermissionToUpdate && (
            <Button
              variant={isEveryRequiredListSummaryPassed ? 'default' : 'outline-disabled'}
              disabled={!isEveryRequiredListSummaryPassed}
              onClick={() => advanceDiagnosticEntry()}
              isLoading={advanceDiagnosticEntryIsPending}
            >
              Enviar diagnóstico e orçamento
            </Button>
          )}
        />

        {isLoading && (
          <MaintenanceWorkflowDiagnosticEntryUpdateLoading />
        )}

        {!isLoading && (
          <ErrorMessageWrapper
            className="flex-1"
            statusCode={!hasPermissionToUpdate ? 403 : forwardingError?.status}
            refetchFunction={refetchForwarding}
          >
            <div className="flex-1 flex gap-6 overflow-hidden">
              <div className="flex-1 overflow-auto">
                <Box className="p-4 flex flex-col gap-4">
                  {isDiagnosticEntryDisapproved && (
                    <ReviewDescription
                      description={forwarding?.directive_entry?.last_review?.description}
                    />
                  )}

                  <div className="flex gap-3">
                    <div className="flex flex-col gap-3 flex-1">
                      <Controller
                        control={control}
                        name="technical_response"
                        render={({ field }) => (
                          <TextArea
                            label="Parecer Técnico"
                            markAsRequired
                            rows={7}
                            {...field}
                            value={field.value || undefined}
                            onBlur={submit}
                            errorMessage={errors.technical_response?.message}
                          />
                        )}
                      />

                      <Controller
                        control={control}
                        name="deadline_to_complete_in_days"
                        render={({ field }) => (
                          <UnitTextField
                            decimals={0}
                            placeholder="Escreva aqui o prazo em dias"
                            unit="dia(s)"
                            label="Prazo em dias para execução da manutenção"
                            markAsRequired
                            {...field}
                            onBlur={submit}
                            errorMessage={errors.deadline_to_complete_in_days?.message}
                          />
                        )}
                      />
                    </div>
                  </div>

                  <DiagnosticEntryUpdateObjectiveAttributes
                    forwarding={forwarding}
                    submit={submit}
                    dataUpdatedAt={forwardingUpdatedAt}
                  />
                </Box>
              </div>

              <div>
                <BoxWithHeader
                  title="Checklist"
                  subtitle={forwarding?.directive_entry?.requirement_list_summary?.description}
                >
                  <div className="flex flex-col gap-2">
                    {forwarding?.directive_entry?.requirement_list_summary?.steps?.map((step) => (
                      <Checkbox
                        key={step.name}
                        label={step.name}
                        name={step.name.replace(/\s/g, '_').toLowerCase()}
                        checked={step.passed}
                        disabled
                        labelClassName="italic text-xs"
                      />
                    ))}
                  </div>

                  <div className="flex flex-col gap-2">
                    <Button
                      variant={isEveryRequiredListSummaryPassed ? 'default' : 'outline-disabled'}
                      disabled={!isEveryRequiredListSummaryPassed}
                      onClick={() => advanceDiagnosticEntry()}
                      isLoading={advanceDiagnosticEntryIsPending}
                    >
                      Enviar diagnóstico e orçamento
                    </Button>
                  </div>
                </BoxWithHeader>
              </div>
            </div>
          </ErrorMessageWrapper>
        )}

        <ModalMessageSimple
          messages={messages}
          setMessages={setMessages}
          isError
        />
      </div>
    </FormProvider>
  );
}

export default MaintenanceWorkflowDiagnosticEntryUpdate;
